import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Save, Library, History, CheckCircle2, Lightbulb, Star, BookOpen, Link2 } from 'lucide-react';
import { MatrixColumns } from './MatrixColumns';
import RichTextEditor from '../RichTextEditor';
import { AttachmentList } from './AttachmentList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FLAG_LABELS = {
  flagged_for_review: 'Review pending',
  accepted: 'Accepted',
  secondary_review: 'Secondary review',
  accepted_modified: 'Accepted (modified)',
  accepted_manual: 'Accepted (manual)',
  manual_required: 'Manual response required',
};
export const DONE_FLAGS = ['accepted', 'accepted_modified', 'accepted_manual'];
const FLAG_STYLE = {
  accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  accepted_modified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  accepted_manual: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  secondary_review: 'bg-violet-50 text-violet-700 border-violet-200',
  flagged_for_review: 'bg-amber-50 text-amber-700 border-amber-200',
  manual_required: 'bg-rose-50 text-rose-700 border-rose-200',
};
const LVL = {
  high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  med: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-rose-50 text-rose-700 border-rose-200',
};
const STRENGTH = {
  strong: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  none: 'bg-rose-50 text-rose-700 border-rose-200',
};
const SELECT_CLS = 'h-8 text-xs border border-slate-200 rounded-md px-1.5 bg-white text-slate-700 focus:outline-none focus:border-[#4ABFCF]';

const ACCENT = {
  strong: 'border-l-emerald-400',
  partial: 'border-l-amber-400',
  none: 'border-l-rose-400',
};

export const qualityOf = (q) => {
  if (!q.response) return 0;
  const s = { low: 40, med: 70, high: 90 };
  const base = ((s[q.confidence] || 50) + (s[q.fit] || 50)) / 2;
  const bonus = q.strength === 'strong' ? 8 : q.strength === 'partial' ? 0 : -8;
  return Math.max(5, Math.min(98, Math.round(base + bonus)));
};

export const ResponseQuestionRow = ({ q, docId, assignees, onUpdate, canManage, onToLibrary, onPromote, selected, onToggleSelect, scaleLevels, extraColumns, projectId, onBrowseStories, onFromNarrative }) => {
  const [text, setText] = useState(q.response || '');
  const [saving, setSaving] = useState(false);
  const [applyingTip, setApplyingTip] = useState(false);
  React.useEffect(() => { setText(q.response || ''); }, [q.response]);

  const applyTip = async () => {
    setApplyingTip(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/rfp/response/projects/${projectId}/documents/${docId}/questions/${q.idx}/apply-tip`,
        {}, { withCredentials: true });
      onUpdate(docId, q.idx, { response: res.data.response });
      setText(res.data.response || '');
      toast.success('Tip applied — review the updated draft (anything to confirm is marked [REP TO CONFIRM])');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not apply the tip'); }
    finally { setApplyingTip(false); }
  };
  const [adding, setAdding] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const dirty = text !== (q.response || '');
  const manualAnswer = q.response && q.response.length >= 30 && !q.library_capability_id && !q.is_pricing
    && (!q.match || ['accepted_manual', 'manual_required'].includes(q.flag));
  const matchedBlockId = q.library_block_id || q.match?.block_id;
  const promotable = !q.is_pricing && !dirty && q.response && q.response.length >= 30 && matchedBlockId
    && ['accepted_modified', 'accepted_manual'].includes(q.flag)
    && (!q.promoted_to_library_at || (q.flag_at && q.flag_at > q.promoted_to_library_at));

  const saveText = async () => {
    setSaving(true);
    // an edited draft becomes "modified"; a manual answer becomes "accepted (manual)"
    const flag = q.flag === 'manual_required' || !q.response ? 'accepted_manual' : 'accepted_modified';
    await onUpdate(docId, q.idx, { response: text, flag });
    setSaving(false);
  };

  return (
    <div className={`border border-slate-200 border-l-4 ${q.is_pricing ? 'border-l-sky-400' : ACCENT[q.strength] || 'border-l-slate-300'} rounded-lg bg-white shadow-sm px-3.5 py-3 space-y-2`}
      data-testid={`rfp-resp-q-${docId}-${q.idx}`}>
      <div className="flex items-start gap-2">
        {onToggleSelect && (
          <input type="checkbox" checked={!!selected} onChange={onToggleSelect}
            className="mt-1 accent-[#0F2040] shrink-0" title="Select for bulk assignment"
            data-testid={`rfp-resp-qsel-${docId}-${q.idx}`} />
        )}
        {q.is_pricing ? (
          <Badge variant="outline" className="text-[10px] mt-0.5 shrink-0 bg-sky-50 text-sky-700 border-sky-200">
            Pricing — review
          </Badge>
        ) : (
          <select value={q.strength || 'none'} onChange={e => onUpdate(docId, q.idx, { strength: e.target.value })}
            title={q.strength_override_by
              ? `Match strength manually set by ${q.strength_override_by}${q.strength_original ? ` — AI originally said "${q.strength_original === 'none' ? 'Gap' : q.strength_original}"` : ''}`
              : 'AI-assessed match strength — change it once you have manually reviewed and updated the answer'}
            className={`h-[22px] mt-0.5 shrink-0 text-[10px] font-medium rounded-md border px-1 cursor-pointer focus:outline-none ${STRENGTH[q.strength] || STRENGTH.none}`}
            data-testid={`rfp-resp-strength-select-${docId}-${q.idx}`}>
            <option value="strong">strong</option>
            <option value="partial">partial</option>
            <option value="none">Gap</option>
          </select>
        )}
        {!q.is_pricing && q.strength_override_by && (
          <span className="text-[9px] mt-1 shrink-0 text-slate-400 italic"
            title={`Manually set by ${q.strength_override_by}`}
            data-testid={`rfp-resp-strength-manual-${docId}-${q.idx}`}>manual</span>
        )}
        {q.response && (
          <Badge variant="outline" title="Answer quality — blend of confidence, fit and match strength; review the weakest first"
            className={`text-[10px] mt-0.5 shrink-0 font-semibold ${qualityOf(q) < 50 ? 'bg-rose-50 text-rose-700 border-rose-200' : qualityOf(q) < 70 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
            data-testid={`rfp-resp-quality-${docId}-${q.idx}`}>
            Q{qualityOf(q)}
          </Badge>
        )}
        {q.cluster_id && (
          <Badge variant="outline" title={q.response_linked ? `Answer copied from the linked requirement (${q.linked_from?.by || 'cluster'}) — edit to make it your own` : 'Overlaps with requirements in other sections — your answer is reused there automatically'}
            className={`text-[10px] mt-0.5 shrink-0 ${q.response_linked ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}
            data-testid={`rfp-resp-linked-${docId}-${q.idx}`}>
            <Link2 className="w-2.5 h-2.5 mr-0.5" />{q.response_linked ? 'reused answer' : 'linked'}
          </Badge>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-[#0F2040] leading-snug">
            <span className="text-slate-400 font-mono font-normal mr-1.5">{q.no || `Q${q.idx + 1}`}</span>{q.title && <span className="text-[#4ABFCF] mr-1">{q.title} —</span>}{q.text}
          </p>
          {q.match && (
            <p className="text-[10px] text-slate-400 mt-0.5">
              → <span className="font-mono">{q.match.capability_id}</span> {q.match.title}
              {q.match.category_names?.length ? ` · ${q.match.category_names.join(', ')}` : ''}
            </p>
          )}
        </div>
      </div>
      {q.gap_note && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1" data-testid={`rfp-resp-gapnote-${docId}-${q.idx}`}>{q.gap_note}</p>
      )}
      {q.reuse && text !== q.reuse.answer && (
        <div className="text-[11px] text-cyan-800 bg-cyan-50 border border-cyan-100 rounded-md px-2 py-1.5 flex items-start gap-2"
          data-testid={`rfp-resp-reuse-${docId}-${q.idx}`}>
          <History className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">
              {q.reuse.win_story && (
                <span className="text-amber-600" title="Starred win story from a won RFP — suggested first"
                  data-testid={`rfp-resp-reuse-winstory-${docId}-${q.idx}`}>★ Win story · </span>
              )}
              Previously accepted in "{q.reuse.project_name}" ({Math.round(q.reuse.similarity * 100)}% similar question)
            </p>
            {q.reuse.win_story && q.reuse.stale && (
              <p className="text-[10px] text-amber-700 font-semibold" data-testid={`rfp-resp-reuse-stale-${docId}-${q.idx}`}>
                Starred over a year ago — re-check this answer is still accurate before reuse.
              </p>
            )}
            <p className="text-cyan-700 line-clamp-2">{q.reuse.answer}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setText(q.reuse.answer)}
            className="h-6 text-[10px] px-2 shrink-0 text-cyan-700 border-cyan-200 hover:bg-cyan-100"
            data-testid={`rfp-resp-reuse-use-${docId}-${q.idx}`}>Use answer</Button>
        </div>
      )}
      <RichTextEditor
        value={text}
        onChange={v => setText((v || '').slice(0, 4000))}
        compact
        rows={3}
        placeholder={q.is_pricing ? 'Pricing question — write the commercial response here…' : 'Manual response required — write the answer here…'}
        className="text-xs"
        data-testid={`rfp-resp-answer-${docId}-${q.idx}`}
      />
      <AttachmentList attachments={q.attachments || []}
        onChange={next => onUpdate(docId, q.idx, { attachments: next })}
        testId={`rfp-resp-attach-${docId}-${q.idx}`} />
      {extraColumns?.length > 0 && q.response && <MatrixColumns q={q} docId={docId} projectId={projectId} extraColumns={extraColumns} onUpdate={onUpdate} />}
      {scaleLevels?.length > 0 && q.response && (
        <div className="flex items-center gap-2 flex-wrap bg-slate-50 border border-slate-100 rounded-md px-2 py-1.5"
          data-testid={`rfp-client-score-row-${docId}-${q.idx}`}>
          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide shrink-0">Client score</span>
          <select value={q.client_score || ''} onChange={e => onUpdate(docId, q.idx, { client_score: e.target.value })}
            className={SELECT_CLS} data-testid={`rfp-client-score-select-${docId}-${q.idx}`}>
            <option value="">—</option>
            {scaleLevels.map(lv => (
              <option key={lv.label} value={lv.label}>{lv.label}{lv.definition ? ` — ${lv.definition.slice(0, 45)}` : ''}</option>
            ))}
          </select>
          {q.client_score && (q.client_score_reviewed ? (
            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"
              title={`Reviewed by ${q.client_score_by || 'rep'}`} data-testid={`rfp-client-score-reviewed-${docId}-${q.idx}`}>
              <CheckCircle2 className="w-2.5 h-2.5 mr-1" />Reviewed
            </Badge>
          ) : (
            <>
              <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200"
                title={q.client_score_rationale || ''} data-testid={`rfp-client-score-ai-${docId}-${q.idx}`}>{q.client_score_source === 'boilerplate' ? 'Suggested from boilerplate' : 'AI suggested'}</Badge>
              <Button size="sm" variant="outline" onClick={() => onUpdate(docId, q.idx, { client_score: q.client_score })}
                className="h-6 text-[10px] px-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                data-testid={`rfp-client-score-confirm-${docId}-${q.idx}`}>Confirm</Button>
            </>
          ))}
          {q.client_score && q.client_score_prev && q.client_score_prev !== q.client_score && (() => {
            const a = parseFloat(q.client_score_prev), b = parseFloat(q.client_score);
            const dir = (!isNaN(a) && !isNaN(b)) ? (b > a ? 'up' : 'down') : 'chg';
            return (
              <span className={`text-[9px] font-semibold rounded-full px-1.5 py-0.5 border ${
                dir === 'up' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                  : dir === 'down' ? 'text-rose-700 bg-rose-50 border-rose-200'
                    : 'text-slate-500 bg-slate-50 border-slate-200'}`}
                title={`Last rescore moved this from ${q.client_score_prev} to ${q.client_score}`}
                data-testid={`rfp-client-score-delta-${docId}-${q.idx}`}>
                {dir === 'up' ? '▲' : dir === 'down' ? '▼' : '•'} was {q.client_score_prev}
              </span>
            );
          })()}
          {q.client_score_rationale && q.client_score && (
            <span className="text-[10px] text-slate-500 italic min-w-0" data-testid={`rfp-client-score-why-${docId}-${q.idx}`}>
              {q.client_score_rationale}
            </span>
          )}
          {q.client_score_tip && (
            <span className="basis-full text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-1.5 py-1 flex items-start gap-1"
              data-testid={`rfp-client-score-tip-${docId}-${q.idx}`}>
              <Lightbulb className="w-3 h-3 shrink-0 mt-[1px]" />
              <span className="min-w-0 flex-1">Tip to lift the score: {q.client_score_tip}</span>
              {q.client_score_tip_applied ? (
                <span className="shrink-0 font-semibold text-emerald-700" data-testid={`rfp-tip-applied-${docId}-${q.idx}`}>
                  Applied ✓
                </span>
              ) : projectId ? (
                <Button size="sm" variant="outline" disabled={applyingTip} onClick={applyTip}
                  className="shrink-0 h-5 text-[9px] px-1.5 border-amber-300 text-amber-700 hover:bg-amber-100"
                  data-testid={`rfp-apply-tip-btn-${docId}-${q.idx}`}>
                  {applyingTip ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply to draft'}
                </Button>
              ) : null}
            </span>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {q.confidence && (
          <select value={q.confidence} onChange={e => onUpdate(docId, q.idx, { confidence: e.target.value })}
            title={q.confidence_override_by
              ? `Confidence manually set by ${q.confidence_override_by}${q.confidence_original ? ` — AI originally said "${q.confidence_original}"` : ''}`
              : 'AI confidence in the drafted answer — change it once you have manually reviewed it'}
            className={`h-[22px] text-[10px] font-medium rounded-md border px-1 cursor-pointer focus:outline-none ${LVL[q.confidence]}`}
            data-testid={`rfp-resp-conf-select-${docId}-${q.idx}`}>
            <option value="high">Confidence: high</option>
            <option value="med">Confidence: med</option>
            <option value="low">Confidence: low</option>
          </select>
        )}
        {q.confidence_override_by && (
          <span className="text-[9px] text-slate-400 italic" title={`Manually set by ${q.confidence_override_by}`}
            data-testid={`rfp-resp-conf-manual-${docId}-${q.idx}`}>manual</span>
        )}
        {q.fit && (
          <select value={q.fit} onChange={e => onUpdate(docId, q.idx, { fit: e.target.value })}
            title={q.fit_override_by
              ? `Fit manually set by ${q.fit_override_by}${q.fit_original ? ` — AI originally said "${q.fit_original}"` : ''}`
              : 'AI-assessed fit between question and answer — change it once you have manually reviewed it'}
            className={`h-[22px] text-[10px] font-medium rounded-md border px-1 cursor-pointer focus:outline-none ${LVL[q.fit]}`}
            data-testid={`rfp-resp-fit-select-${docId}-${q.idx}`}>
            <option value="high">Fit: high</option>
            <option value="med">Fit: med</option>
            <option value="low">Fit: low</option>
          </select>
        )}
        {q.fit_override_by && (
          <span className="text-[9px] text-slate-400 italic" title={`Manually set by ${q.fit_override_by}`}
            data-testid={`rfp-resp-fit-manual-${docId}-${q.idx}`}>manual</span>
        )}
        <Badge variant="outline" className={`text-[10px] ${FLAG_STYLE[q.flag] || ''}`} data-testid={`rfp-resp-flag-badge-${docId}-${q.idx}`}>
          {FLAG_LABELS[q.flag] || q.flag}
        </Badge>
        {q.response_source === 'reuse' && (
          <Badge variant="outline" className="text-[10px] bg-cyan-50 text-cyan-700 border-cyan-200" data-testid={`rfp-resp-reused-badge-${docId}-${q.idx}`}>
            <History className="w-2.5 h-2.5 mr-1" />Reused past answer{q.reuse?.project_name ? ` — ${q.reuse.project_name}` : ''}
          </Badge>
        )}
        {q.library_capability_id && (
          <Badge variant="outline" className="text-[10px] bg-cyan-50 text-cyan-700 border-cyan-200" data-testid={`rfp-resp-inlibrary-${docId}-${q.idx}`}>
            <Library className="w-2.5 h-2.5 mr-1" />In library: {q.library_capability_id}
          </Badge>
        )}
        {onFromNarrative && !q.is_pricing && (
          <button type="button" onClick={() => onFromNarrative(docId, q.idx, q.text)}
            title="Pull a paragraph from a drafted narrative section into this answer"
            className="h-6 px-1.5 rounded-md border text-[10px] flex items-center gap-1 text-slate-400 border-slate-200 hover:text-indigo-700 hover:border-indigo-300 transition-colors"
            data-testid={`rfp-resp-from-narrative-${docId}-${q.idx}`}>
            <BookOpen className="w-3 h-3" />Narrative
          </button>
        )}
        {onBrowseStories && !q.is_pricing && (
          <button type="button" onClick={() => onBrowseStories(docId, q.idx, q.text)}
            title="Browse win stories and insert one into this draft"
            className="h-6 px-1.5 rounded-md border text-[10px] flex items-center gap-1 text-slate-400 border-slate-200 hover:text-cyan-700 hover:border-cyan-300 transition-colors"
            data-testid={`rfp-resp-browse-stories-${docId}-${q.idx}`}>
            <Library className="w-3 h-3" />Stories
          </button>
        )}
        {q.response && (
          <button type="button" onClick={() => onUpdate(docId, q.idx, { starred: !q.starred })}
            title={q.starred
              ? `Win story${q.starred_by ? ` — starred by ${q.starred_by}` : ''} — suggested first on similar future RFPs. Click to unstar.`
              : 'Star as a win story — this answer gets suggested first when drafting similar future RFPs'}
            className={`h-6 px-1.5 rounded-md border text-[10px] font-semibold flex items-center gap-1 transition-colors ${
              q.starred ? 'bg-amber-50 text-amber-700 border-amber-300'
                : 'text-slate-400 border-slate-200 hover:text-amber-600 hover:border-amber-300'}`}
            data-testid={`rfp-resp-star-${docId}-${q.idx}`}>
            <Star className={`w-3 h-3 ${q.starred ? 'fill-amber-400 text-amber-500' : ''}`} />
            {q.starred ? 'Win story' : ''}
          </button>
        )}
        {q.promoted_to_library_at && (
          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"
            title={`This wording was promoted to the library by ${q.promoted_to_library_by || 'a rep'} — the previous version is kept in the block's history (revert from the Library)`}
            data-testid={`rfp-resp-promoted-${docId}-${q.idx}`}>
            <CheckCircle2 className="w-2.5 h-2.5 mr-1" />Promoted
          </Badge>
        )}
        <span className="flex-1" />
        {onPromote && promotable && (
          <Button size="sm" variant="outline" disabled={promoting}
            title={`Update the matched library block (${q.library_capability_id || q.match?.capability_id || ''}) with this improved wording — the previous version stays in the block's history so it can be reverted`}
            onClick={async () => { setPromoting(true); await onPromote(docId, q.idx); setPromoting(false); }}
            className="h-6 text-[10px] px-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`rfp-resp-promote-${docId}-${q.idx}`}>
            {promoting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Library className="w-3 h-3 mr-1" />}Promote to Library
          </Button>
        )}
        {canManage && manualAnswer && !dirty && onToLibrary && (
          <Button size="sm" variant="outline" disabled={adding}
            onClick={async () => { setAdding(true); await onToLibrary(docId, q.idx); setAdding(false); }}
            className="h-6 text-[10px] px-2 text-cyan-700 border-cyan-200 hover:bg-cyan-50" data-testid={`rfp-resp-tolib-${docId}-${q.idx}`}>
            {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Library className="w-3 h-3 mr-1" />}Add to Library
          </Button>
        )}
        <select value={q.flag || ''} onChange={e => onUpdate(docId, q.idx, { flag: e.target.value })}
          className={SELECT_CLS} data-testid={`rfp-resp-flag-select-${docId}-${q.idx}`}>
          {Object.entries(FLAG_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={q.assigned_to || ''} onChange={e => onUpdate(docId, q.idx, { assigned_to: e.target.value })}
          className={SELECT_CLS} data-testid={`rfp-resp-assign-select-${docId}-${q.idx}`}>
          <option value="">Unassigned</option>
          {assignees.map(a => <option key={a.email} value={a.email}>{a.name || a.email}</option>)}
        </select>
        {dirty && (
          <Button size="sm" onMouseDown={e => e.preventDefault()} onClick={saveText} disabled={saving}
            className="h-6 text-[10px] px-2 bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid={`rfp-resp-save-${docId}-${q.idx}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Save
          </Button>
        )}
      </div>
    </div>
  );
};
